import { useCallback, useMemo, useState, type ReactNode } from 'react';
import { BarChart } from '../charts/BarChart';
import { LineChart } from '../charts/LineChart';
import { BarList, DataTable, Drawer, DrawerSection, KeyValues, Segmented } from '../components/data';
import { Gauge } from '../components/Gauge';
import { ErrorBanner, Field, Loading, PageHead } from '../components/ui';
import { downloadCsv, fileStamp } from '../lib/download';
import { formatNumber, num, pct, toLocalDateString, todayLocal } from '../lib/format';
import { useHeldResource } from '../lib/hooks';

type Kind = 'ticket' | 'text' | 'number' | 'hours' | 'money' | 'percent' | 'date' | 'datetime';
type Format = 'percent' | 'number' | 'hours' | 'duration' | 'money' | 'ratio' | 'rating';
type Group = 'Service desk' | 'People & time' | 'Money & contracts' | 'Clients';

/* A drill column. A `t.` key is read from the ticket the row's ticketId names. */
interface Column {
  key: string;
  label: string;
  kind?: Kind;
}
interface Metric {
  key: string;
  group: Group;
  title: string;
  question: string;
  visual: 'gauge' | 'stat' | 'bars';
  value: number | null;
  format: Format;
  foot: string;
  flagged?: boolean;
  gauges?: { label: string; value: number | null; target: number | null; max: number; unit: string; decimals: number }[];
  bars?: { name: string; sub?: string; value: number; display: string }[];
  source: { entities: string; filter: string; fields: string; formula: string; window: string; records: number; note?: string };
  drill: { columns: Column[]; rows: Record<string, unknown>[]; note?: string };
}
interface TicketInfo {
  number: string;
  title: string | null;
  link: string | null;
  company: string | null;
  status: string | null;
  priority: string | null;
  queue: string | null;
  issue: string | null;
  subIssue: string | null;
  source: string | null;
  assigned: string | null;
  created: string | null;
  completed: string | null;
}
/** One ticket that opened or closed in the range: the day of each event, and the queue it sits in. */
interface DailyTicket {
  id: number;
  queue: string;
  opened: string | null;
  closed: string | null;
}
interface Daily {
  title: string;
  question: string;
  queues: { name: string; opened: number; closed: number }[];
  tickets: DailyTicket[];
  source: Metric['source'];
}
interface Payload {
  from: string;
  to: string;
  tz: string;
  metrics: Metric[];
  /** Absent only from a payload cached before the day-by-day card existed. */
  daily?: Daily;
  tickets: Record<string, TicketInfo>;
  fetchedUtc: string;
}

const GROUPS: Group[] = ['Service desk', 'People & time', 'Money & contracts', 'Clients'];
/* Rows drawn at a time in a drill — an open backlog can run to thousands. */
const PAGE = 200;

const day = (iso: string | null) => (iso ? new Date(`${iso.slice(0, 10)}T00:00:00`).toLocaleDateString([], { month: 'numeric', day: 'numeric', year: 'numeric' }) : '—');
const stamp = (iso: string | null) =>
  iso ? new Date(iso).toLocaleString([], { month: 'numeric', day: 'numeric', year: '2-digit', hour: 'numeric', minute: '2-digit' }) : '—';

/** A metric's headline in its own unit. */
function figure(v: number | null, f: Format): string {
  if (v === null) return '—';
  switch (f) {
    case 'percent':
      return pct(v, 1);
    case 'hours':
      return `${num(v, 1)} h`;
    case 'duration':
      return v < 48 ? `${num(v, 1)} hours` : `${num(v / 24, 1)} days`;
    case 'money':
      return formatNumber(v, 'currency');
    case 'ratio':
      return num(v, 2);
    case 'rating':
      return `${num(v, 2)} / 5`;
    default:
      return num(v);
  }
}

function quick(k: 'month' | 'lastMonth' | 'last90' | 'ytd'): { from: string; to: string } {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  if (k === 'month') return { from: toLocalDateString(new Date(d.getFullYear(), d.getMonth(), 1)), to: todayLocal() };
  if (k === 'last90') {
    const s = new Date(d);
    s.setDate(s.getDate() - 89);
    return { from: toLocalDateString(s), to: todayLocal() };
  }
  if (k === 'ytd') return { from: toLocalDateString(new Date(d.getFullYear(), 0, 1)), to: todayLocal() };
  return { from: toLocalDateString(new Date(d.getFullYear(), d.getMonth() - 1, 1)), to: toLocalDateString(new Date(d.getFullYear(), d.getMonth(), 0)) };
}
const QUICK: { k: Parameters<typeof quick>[0]; label: string }[] = [
  { k: 'month', label: 'This month' },
  { k: 'lastMonth', label: 'Last month' },
  { k: 'last90', label: 'Last 90 days' },
  { k: 'ytd', label: 'Year to date' },
];

/** One drill cell's value — from the row, or from its ticket for a `t.` key. */
function cellValue(row: Record<string, unknown>, col: Column, tickets: Payload['tickets']): unknown {
  if (!col.key.startsWith('t.')) return row[col.key];
  const t = tickets[String(row.ticketId)];
  return t ? t[col.key.slice(2) as keyof TicketInfo] : null;
}

/** The same cell as plain text, for search and the CSV. */
function cellText(row: Record<string, unknown>, col: Column, tickets: Payload['tickets']): string {
  const v = cellValue(row, col, tickets);
  if (v === null || v === undefined || v === '') return '';
  if (col.kind === 'ticket') {
    const t = tickets[String(v)];
    return t ? `${t.number}${t.title ? ` ${t.title}` : ''}` : String(v);
  }
  return String(v);
}

function Cell({ row, col, tickets }: { row: Record<string, unknown>; col: Column; tickets: Payload['tickets'] }): ReactNode {
  const v = cellValue(row, col, tickets);
  if (v === null || v === undefined || v === '') return <span className="subtle">—</span>;
  switch (col.kind) {
    case 'ticket': {
      const t = tickets[String(v)];
      if (!t) return <span className="mono xs">{String(v)}</span>;
      return (
        <>
          <div className="xs">
            {t.link ? (
              <a href={t.link} target="_blank" rel="noreferrer">
                {t.number}
              </a>
            ) : (
              t.number
            )}
          </div>
          {t.title && <div className="msp-cell-title">{t.title}</div>}
        </>
      );
    }
    case 'hours':
      return num(Number(v), 2);
    case 'money':
      return formatNumber(Number(v), 'currency');
    case 'percent':
      return pct(Number(v), 1);
    case 'number':
      return formatNumber(Number(v));
    case 'date':
      return day(String(v));
    case 'datetime':
      return stamp(String(v));
    default:
      return String(v);
  }
}

const numeric = (k?: Kind) => k === 'hours' || k === 'money' || k === 'percent' || k === 'number';

/** Where a figure came from, as label and value pairs — on the card and in the drill. */
function SourceList({ s }: { s: Metric['source'] }) {
  return (
    <KeyValues
      items={[
        ['Autotask', <span className="mono">{s.entities}</span>],
        ['Filter', s.filter],
        ['Fields', <span className="mono xs">{s.fields}</span>],
        ['Formula', s.formula],
        ['Window', s.window],
        ['Records', num(s.records)],
        ...(s.note ? ([['Note', s.note]] as [string, ReactNode][]) : []),
      ]}
    />
  );
}

function MetricCard({ n, m, onOpen }: { n: number; m: Metric; onOpen: () => void }) {
  const records = m.drill.rows.length;
  return (
    <section className={`card msp-card${m.gauges && m.gauges.length > 1 ? ' wide' : ''}`}>
      <header className="msp-card-head">
        <span className="msp-n" aria-hidden="true">
          {n}
        </span>
        <div>
          <h2>{m.title}</h2>
          <p className="sub">{m.question}</p>
        </div>
      </header>
      <div className="msp-card-body">
        {m.visual === 'gauge' && m.gauges ? (
          <div className="msp-gauges">
            {m.gauges.map((g) => (
              <Gauge key={g.label} label={g.label} value={g.value} target={g.target} max={g.max} unit={g.unit} decimals={g.decimals} />
            ))}
          </div>
        ) : (
          <div className={`msp-figure${m.flagged ? ' accent' : ''}`}>{figure(m.value, m.format)}</div>
        )}
        {m.visual === 'bars' && m.bars && (m.bars.length ? <BarList rows={m.bars} /> : <p className="sub">Nothing in these days.</p>)}
        <p className={`xs mt-2${m.flagged ? ' msp-flag' : ' muted'}`}>
          {m.flagged && (
            <span className="ui-glyph" aria-hidden="true">
              !{' '}
            </span>
          )}
          {m.foot}
        </p>
      </div>
      <footer className="msp-card-foot">
        <details className="msp-source">
          <summary>
            <span className="msp-source-label">Source</span> {m.source.entities} · {m.source.window}
          </summary>
          <SourceList s={m.source} />
        </details>
        <button type="button" className="btn btn-ghost btn-sm" disabled={!records} onClick={onOpen}>
          {records ? `See the ${num(records)} records →` : 'No records'}
        </button>
      </footer>
    </section>
  );
}

/** The records behind one metric: its source, then every row, searchable and exportable. */
function Drill({ m, d, onClose }: { m: Metric; d: Payload; onClose: () => void }) {
  const [search, setSearch] = useState('');
  const [shown, setShown] = useState(PAGE);
  const q = search.trim().toLowerCase();
  const rows = useMemo(
    () => (q ? m.drill.rows.filter((r) => m.drill.columns.some((c) => cellText(r, c, d.tickets).toLowerCase().includes(q))) : m.drill.rows),
    [m, d, q],
  );
  const exportCsv = () =>
    downloadCsv(`msp_${m.key}_${d.from}_to_${d.to}_${fileStamp()}.csv`, [
      m.drill.columns.map((c) => c.label),
      ...rows.map((r) => m.drill.columns.map((c) => cellText(r, c, d.tickets))),
    ]);

  return (
    <Drawer
      open
      wide
      onClose={onClose}
      title={m.title}
      subtitle={`${m.source.window} · ${figure(m.value, m.format)} · ${m.foot}`}
      footer={
        <div className="row-between wrap">
          <span className="xs muted">
            {num(Math.min(shown, rows.length))} of {num(rows.length)} shown
          </span>
          <button type="button" className="btn btn-secondary btn-sm" disabled={!rows.length} onClick={exportCsv}>
            Export CSV
          </button>
        </div>
      }
    >
      <div className="msp-drill">
        <DrawerSection title="Where it comes from">
          <SourceList s={m.source} />
        </DrawerSection>
        <DrawerSection title="Records" count={num(m.drill.rows.length)}>
          <div className="row wrap mb-2" style={{ alignItems: 'flex-end', gap: 'var(--space-3)' }}>
            <Field label="Search">
              <input
                className="input"
                value={search}
                onChange={(e) => {
                  setSearch(e.target.value);
                  setShown(PAGE);
                }}
                placeholder="Ticket, account, technician"
              />
            </Field>
            {m.drill.note && <p className="xs muted">{m.drill.note}</p>}
          </div>
          {rows.length === 0 ? (
            <p className="sub">Nothing matches.</p>
          ) : (
            <div className="table-wrap">
              <table className="table msp-drill-table">
                <thead>
                  <tr>
                    {m.drill.columns.map((c) => (
                      <th key={c.key} className={numeric(c.kind) ? 'num' : undefined}>
                        {c.label}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {rows.slice(0, shown).map((r, i) => (
                    <tr key={i}>
                      {m.drill.columns.map((c) => (
                        <td key={c.key} className={numeric(c.kind) ? 'num' : c.kind === 'ticket' ? undefined : 'xs'}>
                          <Cell row={r} col={c} tickets={d.tickets} />
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          {rows.length > shown && (
            <button type="button" className="btn btn-ghost btn-sm mt-2" onClick={() => setShown((s) => s + PAGE)}>
              Show {num(Math.min(PAGE, rows.length - shown))} more
            </button>
          )}
        </DrawerSection>
      </div>
    </Drawer>
  );
}

/* ------------------------------------------------------------------ */
/* Day by day — opened against closed, one queue at a time              */
/* ------------------------------------------------------------------ */

type DayRow = { day: string; opened: number; closed: number; net: number };

/** The “every queue” option. Not a queue name, so it cannot collide with one. */
const ALL_QUEUES = '\u0000all';
const DAY_MS = 86_400_000;
const nextDay = (key: string) => new Date(Date.parse(`${key}T00:00:00Z`) + DAY_MS).toISOString().slice(0, 10);
const dayTick = (key: string) => new Date(`${key}T00:00:00`).toLocaleDateString([], { month: 'numeric', day: 'numeric' });
const signed = (v: number) => (v > 0 ? `+${num(v)}` : num(v));

const DAILY_SERIES = [
  { key: 'opened', label: 'Opened', color: 'var(--series-1)' },
  { key: 'closed', label: 'Closed', color: 'var(--series-2)' },
];
/* Past a month of columns the bars are thinner than the gaps between them, so
   the same numbers are drawn as two lines instead. */
const BARS_UP_TO = 31;

const queueLabel = (queue: string) => (queue === ALL_QUEUES ? 'All queues' : queue);

/** The tickets behind one day in the chosen queue — what opened on it, what closed on it. */
function DayDrill({ d, daily, dayKey, queue, onClose }: { d: Payload; daily: Daily; dayKey: string; queue: string; onClose: () => void }) {
  const mine = daily.tickets.filter((t) => queue === ALL_QUEUES || t.queue === queue);
  const opened = mine.filter((t) => t.opened === dayKey);
  const closed = mine.filter((t) => t.closed === dayKey);
  const cols: Column[] = [
    { key: 'ticketId', label: 'Ticket', kind: 'ticket' },
    { key: 't.company', label: 'Account' },
    { key: 'queue', label: 'Queue' },
    { key: 't.priority', label: 'Priority' },
    { key: 't.status', label: 'Status' },
    { key: 't.assigned', label: 'Assigned to' },
  ];
  const rowsOf = (list: DailyTicket[]) => list.map((t) => ({ ticketId: t.id, queue: t.queue }));
  const table = (list: DailyTicket[]) =>
    list.length ? (
      <div className="table-wrap">
        <table className="table msp-drill-table">
          <thead>
            <tr>
              {cols.map((c) => (
                <th key={c.key}>{c.label}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rowsOf(list).map((r) => (
              <tr key={String(r.ticketId)}>
                {cols.map((c) => (
                  <td key={c.key} className={c.kind === 'ticket' ? undefined : 'xs'}>
                    <Cell row={r} col={c} tickets={d.tickets} />
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    ) : (
      <p className="sub">Nothing on this day.</p>
    );

  const exportCsv = () =>
    downloadCsv(`msp_tickets_${dayKey}_${fileStamp()}.csv`, [
      ['Event', ...cols.map((c) => c.label)],
      ...rowsOf(opened).map((r) => ['Opened', ...cols.map((c) => cellText(r, c, d.tickets))]),
      ...rowsOf(closed).map((r) => ['Closed', ...cols.map((c) => cellText(r, c, d.tickets))]),
    ]);

  return (
    <Drawer
      open
      wide
      onClose={onClose}
      title={day(dayKey)}
      subtitle={`${queueLabel(queue)} · ${num(opened.length)} opened · ${num(closed.length)} closed`}
      footer={
        <div className="row-between wrap">
          <span className="xs muted">Autotask&rsquo;s days.</span>
          <button type="button" className="btn btn-secondary btn-sm" disabled={!opened.length && !closed.length} onClick={exportCsv}>
            Export CSV
          </button>
        </div>
      }
    >
      <div className="msp-drill">
        <DrawerSection title="Opened" count={num(opened.length)}>
          {table(opened)}
        </DrawerSection>
        <DrawerSection title="Closed" count={num(closed.length)}>
          {table(closed)}
        </DrawerSection>
      </div>
    </Drawer>
  );
}

/**
 * Beyond the twenty: tickets opened against closed, a point per day, for one
 * queue over any stretch of the report’s range.
 *
 * Both filters are local. The queue and the days shown are cut from the
 * tickets the report already carries (`daily`, in routes/reportsMspMetrics.ts),
 * so changing either redraws at once and never re-reads Autotask; only the
 * range at the top of the page does that, and it is the outer bound here.
 * Up to a month is drawn as grouped columns, longer as two lines, and a day
 * opens the tickets counted on it.
 */
function DailyTickets({ d, daily }: { d: Payload; daily: Daily }) {
  const [queue, setQueue] = useState(ALL_QUEUES);
  const [lo, setLo] = useState(d.from);
  const [hi, setHi] = useState(d.to);
  const [view, setView] = useState<'chart' | 'table'>('chart');
  const [openDay, setOpenDay] = useState<string | null>(null);
  const closeDay = useCallback(() => setOpenDay(null), []);
  /* A typed date outside the range the report was read for has no tickets behind it. */
  const clamp = (v: string) => (!v ? v : v < d.from ? d.from : v > d.to ? d.to : v);

  const days = useMemo(() => {
    const out: string[] = [];
    for (let k = lo; k && k <= hi; k = nextDay(k)) out.push(k);
    return out;
  }, [lo, hi]);

  const rows = useMemo(() => {
    const byDay = new Map<string, DayRow>(days.map((k) => [k, { day: k, opened: 0, closed: 0, net: 0 }]));
    for (const t of daily.tickets) {
      if (queue !== ALL_QUEUES && t.queue !== queue) continue;
      if (t.opened) {
        const r = byDay.get(t.opened);
        if (r) r.opened += 1;
      }
      if (t.closed) {
        const r = byDay.get(t.closed);
        if (r) r.closed += 1;
      }
    }
    const out = [...byDay.values()];
    for (const r of out) r.net = r.opened - r.closed;
    return out;
  }, [daily, queue, days]);

  const opened = rows.reduce((n, r) => n + r.opened, 0);
  const closed = rows.reduce((n, r) => n + r.closed, 0);
  const net = opened - closed;
  const busiest = rows.reduce<DayRow | null>((best, r) => (best && best.opened >= r.opened ? best : r), null);
  const zoomed = lo !== d.from || hi !== d.to;
  const empty = 'No days in this stretch.';

  const exportCsv = () =>
    downloadCsv(`msp_tickets_per_day_${lo}_to_${hi}_${fileStamp()}.csv`, [
      ['Day', 'Queue', 'Opened', 'Closed', 'Net'],
      ...rows.map((r) => [r.day, queueLabel(queue), r.opened, r.closed, r.net]),
    ]);

  return (
    <>
      <section className="card msp-card">
        <header className="msp-card-head">
          <span className="msp-n" aria-hidden="true">
            +
          </span>
          <div>
            <h2>{daily.title}</h2>
            <p className="sub">{daily.question}</p>
          </div>
        </header>
        <div className="msp-card-body">
          <div className="row wrap mb-2" style={{ alignItems: 'flex-end', gap: 'var(--space-3)' }}>
            <Field label="Queue">
              <select className="input" value={queue} onChange={(e) => setQueue(e.target.value)}>
                <option value={ALL_QUEUES}>All queues</option>
                {daily.queues.map((q) => (
                  <option key={q.name} value={q.name}>
                    {q.name} — {num(q.opened)} opened, {num(q.closed)} closed
                  </option>
                ))}
              </select>
            </Field>
            <Field label="Days from" hint={`within ${day(d.from)} – ${day(d.to)}`}>
              <input className="input" type="date" value={lo} min={d.from} max={hi} onChange={(e) => setLo(clamp(e.target.value))} />
            </Field>
            <Field label="Days to">
              <input className="input" type="date" value={hi} min={lo} max={d.to} onChange={(e) => setHi(clamp(e.target.value))} />
            </Field>
            <button
              type="button"
              className="btn btn-ghost btn-sm"
              disabled={!zoomed}
              onClick={() => {
                setLo(d.from);
                setHi(d.to);
              }}
            >
              Whole range
            </button>
            <Field label="Show">
              <Segmented
                label="Day by day view"
                value={view}
                onChange={setView}
                options={[
                  { id: 'chart' as const, label: 'Chart' },
                  { id: 'table' as const, label: 'Table' },
                ]}
              />
            </Field>
            <button type="button" className="btn btn-secondary btn-sm" disabled={!rows.length} onClick={exportCsv}>
              Export CSV
            </button>
          </div>

          {view === 'table' ? (
            <DataTable<DayRow>
              rows={rows}
              rowKey={(r) => r.day}
              empty={empty}
              columns={[
                { key: 'day', label: 'Day', render: (r) => day(r.day) },
                { key: 'opened', label: 'Opened', align: 'right', render: (r) => num(r.opened) },
                { key: 'closed', label: 'Closed', align: 'right', render: (r) => num(r.closed) },
                { key: 'net', label: 'Net', align: 'right', render: (r) => signed(r.net) },
              ]}
            />
          ) : rows.length <= BARS_UP_TO ? (
            <BarChart data={rows} xKey="day" series={DAILY_SERIES} height={260} xFormat={dayTick} emptyMessage={empty} onSelect={(r) => setOpenDay(r.day)} />
          ) : (
            <LineChart data={rows} xKey="day" series={DAILY_SERIES} height={260} zeroBased xFormat={dayTick} emptyMessage={empty} onSelect={(r) => setOpenDay(r.day)} />
          )}

          <p className={`xs mt-2${net > 0 ? ' msp-flag' : ' muted'}`}>
            {net > 0 && (
              <span className="ui-glyph" aria-hidden="true">
                !{' '}
              </span>
            )}
            {queueLabel(queue)} · {num(opened)} opened · {num(closed)} closed · net {signed(net)} over {num(days.length)}{' '}
            {days.length === 1 ? 'day' : 'days'} · {num(days.length ? opened / days.length : 0, 1)} opened a day
            {busiest && busiest.opened > 0 ? ` · busiest ${day(busiest.day)}, ${num(busiest.opened)} opened` : ''}
          </p>
        </div>
        <footer className="msp-card-foot">
          <details className="msp-source">
            <summary>
              <span className="msp-source-label">Source</span> {daily.source.entities} · {daily.source.window}
            </summary>
            <SourceList s={daily.source} />
          </details>
          <span className="xs muted">{view === 'chart' ? 'Pick a day to open its tickets.' : 'Switch to Chart to open a day.'}</span>
        </footer>
      </section>

      {openDay && <DayDrill key={openDay} d={d} daily={daily} dayKey={openDay} queue={queue} onClose={closeDay} />}
    </>
  );
}

/**
 * Reports › MSP Top 20 — the twenty figures an MSP reports on, for the days
 * chosen, read live from Autotask (routes/reportsMspMetrics.ts): service desk,
 * people and time, money and contracts, and clients.
 *
 * Each card says where its number came from — the Autotask entities, filter,
 * fields, formula and window, under Source — and opens the records behind it
 * in a drawer, ticket numbers linked to Autotask. Read once per range and
 * kept until Refresh.
 */
export function MspMetricsReportPage() {
  const [{ from, to }, setRange] = useState(() => quick('lastMonth'));
  const [draftFrom, setDraftFrom] = useState(from);
  const [draftTo, setDraftTo] = useState(to);
  const [group, setGroup] = useState<'all' | Group>('all');
  const [openKey, setOpenKey] = useState<string | null>(null);
  /* Stable, so the drawer does not re-take focus on every render. */
  const close = useCallback(() => setOpenKey(null), []);
  /* The browser's zone, for deciding which tickets came in after hours. */
  const tz = useMemo(() => Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC', []);
  const data = useHeldResource<Payload>(`/reports/msp-metrics?${new URLSearchParams({ from, to, tz })}`);
  const d = data.data;

  const apply = (r: { from: string; to: string }) => {
    setDraftFrom(r.from);
    setDraftTo(r.to);
    setRange(r);
  };

  /* Numbered in the page's order, so "number 14" means the same card whichever group is showing. */
  const numbered = (d?.metrics ?? []).map((m, i) => ({ n: i + 1, m }));
  const shown = numbered.filter(({ m }) => group === 'all' || m.group === group);
  const opened = d?.metrics.find((m) => m.key === openKey) ?? null;

  const exportSummary = () =>
    d &&
    downloadCsv(`msp_top20_${d.from}_to_${d.to}_${fileStamp()}.csv`, [
      ['#', 'Group', 'Metric', 'Value', 'Detail', 'Autotask', 'Filter', 'Fields', 'Formula', 'Window', 'Records', 'Note'],
      ...numbered.map(({ n, m }) => [
        n, m.group, m.title, figure(m.value, m.format), m.foot, m.source.entities, m.source.filter, m.source.fields, m.source.formula,
        m.source.window, m.source.records, m.source.note ?? '',
      ]),
    ]);

  return (
    <>
      <PageHead
        title="MSP Top 20"
        lede="The twenty figures an MSP reports on — SLA, speed, backlog, utilization, revenue, contracts and client load — read live from Autotask. Every card says where its number came from and opens the records behind it."
      />

      <section className="card mb-4">
        <div className="card-body">
          <div className="row wrap" style={{ alignItems: 'flex-end', gap: 'var(--space-3)' }}>
            <Field label="From">
              <input className="input" type="date" value={draftFrom} max={draftTo} onChange={(e) => setDraftFrom(e.target.value)} />
            </Field>
            <Field label="To">
              <input className="input" type="date" value={draftTo} min={draftFrom} onChange={(e) => setDraftTo(e.target.value)} />
            </Field>
            <button
              type="button"
              className="btn btn-primary btn-sm"
              disabled={!draftFrom || !draftTo || draftFrom > draftTo || (draftFrom === from && draftTo === to)}
              onClick={() => setRange({ from: draftFrom, to: draftTo })}
            >
              Show
            </button>
            <div className="row wrap" style={{ gap: 'var(--space-1)' }}>
              {QUICK.map((k) => (
                <button key={k.k} type="button" className="btn btn-ghost btn-sm" onClick={() => apply(quick(k.k))}>
                  {k.label}
                </button>
              ))}
            </div>
          </div>
          <div className="row wrap mt-2" style={{ alignItems: 'flex-end', gap: 'var(--space-3)' }}>
            <Field label="Show">
              <Segmented
                label="Show"
                value={group}
                onChange={setGroup}
                options={[{ id: 'all' as const, label: 'All 20' }, ...GROUPS.map((g) => ({ id: g, label: g }))]}
              />
            </Field>
            <button type="button" className="btn btn-secondary btn-sm" disabled={!d} onClick={exportSummary}>
              Export summary CSV
            </button>
            <button type="button" className="btn btn-ghost btn-sm" disabled={data.refreshing} onClick={data.refresh}>
              {data.refreshing ? 'Refreshing…' : 'Refresh from Autotask'}
            </button>
          </div>
          <p className="xs muted mt-2">
            {day(from)} – {day(to)}, Autotask&rsquo;s days. Backlog, aging, workload and endpoints are as of now; contracts look 90 days ahead.
            Revenue is what Autotask has posted, so a whole, closed month reads truest. Targets on the dials are common MSP starting points, named
            under each card&rsquo;s Source.
            {data.fetchedAt &&
              ` As of ${new Date(data.fetchedAt).toLocaleString([], { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}.`}
          </p>
        </div>
      </section>

      {data.error && <ErrorBanner message={data.error} />}
      {data.loading && !d && <Loading label="Reading tickets, time, billing and contracts from Autotask" />}

      {d && d.daily && (group === 'all' || group === 'Service desk') && (
        <div className="mb-4">
          <h2 className="msp-group">Day by day</h2>
          <DailyTickets key={`${d.from}|${d.to}`} d={d} daily={d.daily} />
        </div>
      )}

      {d &&
        GROUPS.filter((g) => shown.some(({ m }) => m.group === g)).map((g) => (
          <div key={g} className="mb-4">
            <h2 className="msp-group">{g}</h2>
            <div className="msp-grid">
              {shown
                .filter(({ m }) => m.group === g)
                .map(({ n, m }) => (
                  <MetricCard key={m.key} n={n} m={m} onOpen={() => setOpenKey(m.key)} />
                ))}
            </div>
          </div>
        ))}

      {d && opened && <Drill key={opened.key} m={opened} d={d} onClose={close} />}
    </>
  );
}
